import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import apiClient from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { esSuperAdmin } from '../utils/roles';

// 3 sep 2026 (Augusto: "voy a necesitar cuentas gratis, las que serán
// otorgadas a agente para hacer testing" -> "necesito un botón de
// suscripción separado para testers" -> "no, lo quiero como otra
// sección dentro de ADMIN"): esta tarjeta vivió primero en
// Subscription.js (la página que ve cualquier cliente que paga) y
// después como tarjeta dentro de ClientesPage.js -- Augusto pidió
// explícitamente que fuera su PROPIA sección dentro de ADMIN, no algo
// metido dentro de otra página. Ver NotionSidebar.js (sección ADMIN,
// ahora con dos links) y routes.js (ruta /testers).
//
// Mismo set que PLANES_COMPABLES en endpoints/subscription.py (debe
// mantenerse igual) -- "free" no está porque no tendría sentido
// "regalar" el plan que ya tiene todo el mundo por defecto.
// 17 sep 2026 (Augusto: "no tenemos ya Negocio en PCM, se maneja por
// Capo"): quedan solo los planes que PCM realmente vende. Regalar un
// plan que el producto ya no reconoce era un resto del cambio anterior.
const PLANES_REGALABLES = [
  { id: 'pro', nombre: 'Pro' },
  { id: 'personal', nombre: 'Personal' },
];

const TestersPage = () => {
  const { user } = useAuth();
  const [compEmail, setCompEmail] = useState('');
  const [compPlan, setCompPlan] = useState('pro');
  const [compLoading, setCompLoading] = useState(false);
  const [aviso, setAviso] = useState(null); // { severity, mensaje }
  const [confirmacion, setConfirmacion] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);

  // Recarga la data de demostración de ESTA cuenta (POST
  // /admin/demo/recargar). El endpoint no recibe email ni id: opera
  // siempre sobre quien llama, así que no hay forma de apuntarlo a la
  // cuenta de otra persona. La palabra de confirmación se valida también
  // en el backend -- acá solo sirve para deshabilitar el botón.
  const handleRecargarDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await apiClient.post('/admin/demo/recargar', {
        confirmacion: confirmacion.trim().toUpperCase(),
      });
      const c = res.data?.creado || {};
      setAviso({
        severity: 'success',
        mensaje: `Listo -- ${c.transacciones} transacciones en ${c.meses} meses, ${c.cuentas} cuentas y ${c.deudas} deudas.`,
      });
      setConfirmacion('');
    } catch (err) {
      setAviso({
        severity: 'error',
        mensaje: err.response?.data?.detail || 'No se pudo recargar la data de demostración.',
      });
    } finally {
      setDemoLoading(false);
    }
  };

  // Regala un plan Pro+ sin cobrar (POST /subscription/comp, ver
  // endpoints/subscription.py) -- deja la cuenta EXACTAMENTE como la de
  // un cliente real que pagó (is_paid=True + subscription_plan=el
  // elegido), sin ningún mecanismo de bypass aparte. No hay chequeo de
  // email acá: el gate real vive en el backend
  // (Depends(get_current_super_admin) en POST /subscription/comp) -- si
  // alguien sin acceso llamara al endpoint a mano, igual lo rechaza con
  // 403.
  //
  // 17 sep 2026: además del link oculto en la barra lateral, la página
  // se protege sola (ver el early return más abajo). Esconder el link no
  // alcanza: /testers se puede escribir a mano en la barra de
  // direcciones, y llegar a un formulario que promete regalar planes y
  // solo falla al apretar el botón es peor experiencia que decirlo de
  // entrada.
  const handleCompAccount = async () => {
    const email = compEmail.trim();
    if (!email) return;
    setCompLoading(true);
    try {
      const res = await apiClient.post(
        `/subscription/comp?email=${encodeURIComponent(email)}&plan=${encodeURIComponent(compPlan)}`
      );
      setAviso({ severity: 'success', mensaje: `Listo -- ${res.data.email} ahora tiene el plan ${res.data.plan}, sin cobrar nada.` });
      setCompEmail('');
    } catch (err) {
      setAviso({
        severity: 'error',
        mensaje: err.response?.data?.detail || 'No se pudo regalar el acceso.',
      });
    } finally {
      setCompLoading(false);
    }
  };

  if (!esSuperAdmin(user)) {
    return (
      <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
        <Alert severity="info">
          Las cuentas de testing se administran desde la cuenta de la empresa
          (augusto@darwinoptima.com).
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Cuentas de testing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Regala acceso completo a una cuenta ya registrada, sin cobrar nada -- para agentes o
        personas haciendo testing de la app.
      </Typography>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CardGiftcardIcon fontSize="small" color="action" />
            <Typography variant="h6">Regalar acceso</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Marca una cuenta ya registrada como pagada, sin cobrar nada, y le acredita 500 páginas
            extra para que no se le acabe la cuota a mitad de la prueba. No le da acceso a este
            panel de administración — eso depende únicamente del email de la cuenta, sin relación
            con esto.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
            <TextField
              label="Email de la cuenta"
              placeholder="tester@ejemplo.com"
              size="small"
              value={compEmail}
              onChange={(e) => setCompEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCompAccount();
                }
              }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="comp-plan-label">Plan a regalar</InputLabel>
              <Select
                labelId="comp-plan-label"
                label="Plan a regalar"
                value={compPlan}
                onChange={(e) => setCompPlan(e.target.value)}
              >
                {PLANES_REGALABLES.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={compLoading || !compEmail.trim()}
              onClick={handleCompAccount}
            >
              {compLoading ? 'Regalando…' : 'Regalar acceso'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* 17 sep 2026 (Augusto: "la cuenta administradora debería tener ya
          data para poder usarla para demos"). Vive acá y no en una página
          propia porque es la misma naturaleza que lo de arriba -- cosas
          que solo hace la cuenta de la empresa -- y no justifica una
          entrada más en el menú. */}
      <Card sx={{ mt: 3, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <RestartAltIcon fontSize="small" color="action" />
            <Typography variant="h6">Recargar data de demostración</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Llena <strong>esta</strong> cuenta con 12 meses de finanzas de ejemplo: cuentas,
            tarjetas, préstamo, transacciones, presupuestos y metas de ahorro. Sirve para que en una
            demo el tablero, los informes y el calendario tengan algo que mostrar.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esto <strong>borra todo</strong> lo que haya hoy en esta cuenta antes de volver a
            llenarla. Nunca toca la cuenta de otra persona. No se puede deshacer.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
            <TextField
              label="Escribe RECARGAR DEMO para confirmar"
              size="small"
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Button
              variant="outlined"
              color="warning"
              disabled={demoLoading || confirmacion.trim().toUpperCase() !== 'RECARGAR DEMO'}
              onClick={handleRecargarDemo}
            >
              {demoLoading ? 'Cargando…' : 'Recargar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={!!aviso}
        autoHideDuration={5000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {aviso ? <Alert severity={aviso.severity} onClose={() => setAviso(null)}>{aviso.mensaje}</Alert> : undefined}
      </Snackbar>
    </Container>
  );
};

export default TestersPage;
